import { useLazyQuery } from "@apollo/client/react";
import { capitalizeWords, consoleBeauty } from "data/functions";
import { useCallback, useRef } from "react";
import { logGQLError } from "../gql-functions";

export function useQueryWithEffect(query, options = {}) {
  const {
    onCalled, // срабатывает при любом результате (успешном, ошибке, или отмене)
    onCompleted, // для обратной совместимости — только при успехе
    onError,
    onLoading,
    refetchLimit,
    ...otherOptions
  } = options;

  const [refetch, result] = useLazyQuery(query, {
    ...otherOptions,
    // Не передаём колбэки здесь — мы их обработаем сами
    // onCompleted: undefined,
    // onError: undefined,
  });

  const lastRefetchTime = useRef(0);
  const isFetching = useRef(false);

  const safeRefetch = useCallback(
    async (variables) => {
      const now = Date.now();
      const queryName = capitalizeWords(query?.definitions?.[0]?.name?.value);

      // 1. Проверка: идёт ли сейчас запрос?
      if (isFetching.current && refetchLimit?.rejectConcurrent) {
        logGQLError(queryName, {
          message: `Запрос уже выполняется`,
        });
        return { data: undefined, error: undefined, loading: false };
      }

      // 2. Проверка: слишком частый вызов?
      if (refetchLimit?.minDelay) {
        const delay = refetchLimit.minDelay;
        if (now - lastRefetchTime.current < delay) {
          logGQLError(queryName, {
            message: `Слишком частый вызов (мин. задержка: ${delay}мс)`,
          });
          return { data: undefined, error: undefined, loading: false };
        }
      }

      // Устанавливаем флаги
      isFetching.current = true;
      lastRefetchTime.current = now;

      try {
        // Вызываем refetch и ждём результат
        const refetchResult = await refetch(variables);

        // if (refetchResult?.data?.tsd_getEvent) {
        //   console.log(
        //     refetchResult.data.tsd_getEvent.type +
        //       "  ---- " +
        //       refetchResult.data.tsd_getEvent.state,
        //   );
        // }

        // Вызываем обработчики
        if (refetchResult?.loading && onLoading) {
          onLoading();
        }

        if (!refetchResult?.loading && refetchResult?.data && onCompleted) {
          onCompleted(refetchResult.data);
        }

        if (!refetchResult?.loading && refetchResult?.error && onError) {
          onError(refetchResult.error);
        }

        if (onCalled) {
          onCalled({
            data: refetchResult?.data,
            error: refetchResult?.error,
            loading: refetchResult?.loading,
          });
        }

        return refetchResult;
      } catch (error_) {
        if ((error_?.message || "").includes("Invalid or expired token")) {
          // return { data: undefined, error: null, loading: false };
        }

        const errorResult = {
          data: undefined,
          error: error_,
          loading: false,
        };

        logGQLError(queryName, error_);

        if (onError) {
          onError(error_);
        }

        if (onCalled) {
          onCalled({
            data: undefined,
            error: error_,
            loading: false,
          });
        }

        return errorResult;
      } finally {
        isFetching.current = false;
      }
    },
    [refetch, refetchLimit, onCompleted, onError, onLoading, onCalled],
  );

  // Возвращаем только safeRefetch и raw result
  return [safeRefetch, result];
}
